import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Buyer';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const BUYER_API_REDUCER_KEY = 'buyers';
export const BUYER_PATH = 'buyers';

export const buyerApi = createApi({
    reducerPath: BUYER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Buyer', 'Buyers'],
    endpoints: (builder) => ({
        getBuyerList: builder.query<PaginationResult<Entity.Buyer>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ BUYER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Buyers'],
        }),
        getBuyer: builder.query<Entity.Buyer, Entity.PrimaryBuyer>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = buyerNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ BUYER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Buyer'],
        }),
        getBuyerMultiple: builder.query<Entity.Buyer, Entity.PrimaryBuyer[]>({
            query: (ids) => {
                return ({
                    url: `/${ BUYER_PATH }/ids${Entity.PrimaryBuyer.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Buyers'],
        }),
        getBuyerPresentation: builder.query<PaginationResult<Entity.Buyer>, Entity.PresentationBuyerRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ BUYER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Buyers'],
        }),
        
        insertBuyer: builder.mutation<Entity.Buyer, Entity.CreateBuyer>({
            query: (toInsert) => {
                return ({
                    url: `/${ BUYER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    buyerApi.util.upsertQueryData('getBuyer', Entity.PrimaryBuyer.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Buyers'],
        }),
        insertMultipleBuyer: builder.mutation<Entity.Buyer[], Entity.CreateBuyer[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ BUYER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            buyerApi.util.upsertQueryData('getBuyer', Entity.PrimaryBuyer.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Buyers'],
        }),
        updateBuyer: builder.mutation<Entity.Buyer, Entity.UpdateEntityBuyer>({
            query: (toUpdate) => {
                return ({
                    url: `/${ BUYER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Buyers'],
        }),
        updateMultipleBuyer: builder.mutation<Entity.Buyer[], Entity.UpdateEntityBuyer[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ BUYER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Buyers'],
        }),
        deleteBuyer: builder.mutation<Entity.Buyer, Entity.Buyer>({
            query: (toDelete) => {
                return ({
                    url: `/${ BUYER_PATH }/${Entity.PrimaryBuyer.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Buyers'],
        }),
        deleteMultipleBuyer: builder.mutation<Entity.Buyer[], Entity.PrimaryBuyer[]>({
            query: (ids) => {
                return ({
                    url: `/${ BUYER_PATH }/batch${Entity.PrimaryBuyer.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Buyers'],
        }),
    }),
})

export const [buyerNormalizer, buyerNormalizerMiddleware] = createNormalizationMiddleware(BUYER_API_REDUCER_KEY, buyerApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetBuyerListQuery,
    useGetBuyerMultipleQuery,
    useGetBuyerPresentationQuery,
    useGetBuyerQuery,
    useInsertBuyerMutation,
    useInsertMultipleBuyerMutation,
    useUpdateBuyerMutation,
    useUpdateMultipleBuyerMutation,
    useDeleteBuyerMutation,
    useDeleteMultipleBuyerMutation,
    
} = buyerApi
